import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from 'data-fetching-client';
import debounce from 'transmute/debounce';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RightIcon } from 'foundations-assets';
import { View } from 'foundations-components/components/View';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { UNKNOWN_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import SelectionPanel from 'automation-ui-canvas/create/SelectionPanel';
import WorkflowTypeSelect from 'automation-ui-enrollment/components/workflowType/WorkflowTypeSelect';
import { convertToAddonPropertyOption } from 'automation-ui-action-features/object-type-properties/propertyUtils';
import { dataTypeToIconName } from 'automation-ui-formula-editor/data-sources/utils/getPropertyIcon';
import { SEARCH_PROPERTIES_USING_NLP, RECORD_PROPERTY_CLICKED_EVENT } from 'automation-ui-canvas/create/queries/nlpPropertySearchQueries';
import useWorkflowTypePQLs from 'automation-ui-enrollment/components/workflowType/hooks/useWorkflowTypePQLs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledIconContainer = styled.span.withConfig({
  displayName: "PropertySelectionObjectAgnosticPanel__StyledIconContainer",
  componentId: "sc-8pjhuv-0"
})(["margin:0px ", ";font-size:0.625rem;"], ({
  theme
}) => theme.space['100']);
const EMPTY_PROPERTY_MATCHES = [];
export default function PropertySelectionObjectAgnosticPanel({
  setObjectTypeId,
  onClose,
  stagedTriggerOption,
  onSelectTriggerOption,
  searchTerm,
  setSearchTerm
}) {
  var _propertyData$searchP;
  useEffect(() => {
    trackEvent(Trackables.VIEWED_PROPERTY_TRIGGER_SELECTION_PANEL, {
      type: 'object-agnostic',
      objectTypeId: UNKNOWN_ID
    });
  }, []);
  const [searchTermToQuery, setSearchTermToQuery] = useState(searchTerm);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [allOptions, setAllOptions] = useState([]);
  const debouncedSetSearchTermToQuery = useMemo(() => debounce(300, newSearchTerm => {
    setOffset(0);
    setAllOptions([]);
    setIsLoading(true);
    setSearchTermToQuery(newSearchTerm);
  }), []);
  const handleSeachTermChange = newSearchTerm => {
    setSearchTerm(newSearchTerm);
    debouncedSetSearchTermToQuery(newSearchTerm);
  };
  const {
    options: objectTypeOptions,
    loading: pqlsLoading,
    isLocked,
    shouldSelectObjectType,
    CustomModal,
    onCloseCustomModal
  } = useWorkflowTypePQLs();
  const objectTypeNameById = useMemo(() => new Map(objectTypeOptions.map(({
    value,
    text
  }) => [value, text])), [objectTypeOptions]);
  const objectTypeIdAllowList = useMemo(() => Array.from(objectTypeNameById.keys()), [objectTypeNameById]);
  const {
    data: propertyData
  } = useQuery(SEARCH_PROPERTIES_USING_NLP, {
    variables: {
      searchTerm: searchTermToQuery,
      objectTypeIdAllowList,
      pageLength: 15,
      resultLimit: 30,
      similarityThreshold: 0.75,
      offset
    }
  });
  const [recordPropertyClickedEvent] = useMutation(RECORD_PROPERTY_CLICKED_EVENT);
  const properties = propertyData === null || propertyData === void 0 ? void 0 : propertyData.searchPropertiesUsingNlp.results;
  const hasMore = propertyData === null || propertyData === void 0 ? void 0 : propertyData.searchPropertiesUsingNlp.hasMore;
  const nextOffset = (_propertyData$searchP = propertyData === null || propertyData === void 0 ? void 0 : propertyData.searchPropertiesUsingNlp.offset.index) !== null && _propertyData$searchP !== void 0 ? _propertyData$searchP : 0;
  useEffect(() => {
    if (!properties) {
      return;
    }
    const newOptions = (properties !== null && properties !== void 0 ? properties : EMPTY_PROPERTY_MATCHES).map(({
      objectTypeId,
      property: _property
    }) => {
      var _option$icon;
      const property = _property;
      const option = convertToAddonPropertyOption({
        property,
        objectTypeId,
        enforceFlp: false
      });
      return Object.assign({}, option, {
        value: `${objectTypeId}.${option.value}`,
        propertyName: option.value,
        icon: (_option$icon = option.icon) !== null && _option$icon !== void 0 ? _option$icon : dataTypeToIconName(property.type),
        locked: isLocked(objectTypeId),
        indicator: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx("strong", {
            children: objectTypeNameById.get(objectTypeId)
          }), /*#__PURE__*/_jsx(StyledIconContainer, {
            children: /*#__PURE__*/_jsx(RightIcon, {
              color: "text-core-subtle"
            })
          }), /*#__PURE__*/_jsx("span", {
            children: option.text
          })]
        })
      });
    });
    const startIndex = nextOffset - properties.length;
    setAllOptions(currentOptions => currentOptions.slice(0, startIndex).concat(newOptions));
    setIsLoading(false);
  }, [properties, nextOffset, isLocked, objectTypeNameById]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [CustomModal && /*#__PURE__*/_jsx(CustomModal, {
      onClose: onCloseCustomModal
    }), /*#__PURE__*/_jsx(SelectionPanel, {
      heading: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "createWorkflow.propertySelectionPanel.title"
      }),
      additionalFilters: /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsx(WorkflowTypeSelect, {
          objectTypeId: UNKNOWN_ID,
          onChange: setObjectTypeId,
          placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "createWorkflow.objectSelectPlaceholder"
          }),
          noneOptionLabel: I18n.text('createWorkflow.objectTypeSelectionPanel.default.property'),
          objectTypeFilter: () => true
        })
      }),
      options: allOptions,
      loading: isLoading || pqlsLoading,
      searchTerm: searchTerm,
      onSearchTermChange: handleSeachTermChange,
      onClose: onClose,
      onSelectOption: ({
        propertyName,
        objectTypeId
      }) => {
        if (shouldSelectObjectType(objectTypeId)) {
          void recordPropertyClickedEvent({
            variables: {
              searchTerm: searchTermToQuery,
              selectedProperty: {
                propertyName,
                objectTypeId: objectTypeId
              },
              searchResults: allOptions.map(option => ({
                propertyName: option.propertyName,
                objectTypeId: option.objectTypeId
              })),
              objectTypeIdAllowList
            }
          });
          trackEvent(Trackables.SELECTED_PROPERTY_TRIGGER, {
            type: 'object-agnostic',
            objectTypeId
          });
          onSelectTriggerOption(Object.assign({}, stagedTriggerOption, {
            filters: [{
              filterType: 'PROPERTY',
              property: 'hs_name',
              operation: {
                propertyType: 'string',
                operator: 'IS_EQUAL_TO',
                value: propertyName,
                defaultValue: null,
                includeObjectsWithNoValueSet: false,
                operationType: 'string',
                operatorName: 'IS_EQUAL_TO'
              },
              frameworkFilterId: null
            }]
          }));
          setObjectTypeId(objectTypeId);
        }
      },
      hasMore: hasMore,
      loadMore: () => {
        setOffset(nextOffset);
        setIsLoading(true);
      }
    })]
  });
}